import bundledAxios from 'axios';


let relayToken = '';
let relayTokenExpiresAt = 0;
let relayTokenPromise = null;
let relayTokenOrganization = '';
const installedClients = new WeakSet();


export function isSwanbotRelayEnabled() {
  return !!(window.WingmanState && window.WingmanState.swanbot_relay_enabled);
}


export async function getSwanbotRelayToken() {
  if (!isSwanbotRelayEnabled()) {
    return '';
  }

  const organization = document.getElementById('customer_id')?.value || '';
  if (relayTokenOrganization !== organization) {
    relayToken = '';
    relayTokenExpiresAt = 0;
    relayTokenPromise = null;
    relayTokenOrganization = organization;
  }
  if (relayToken && Date.now() < relayTokenExpiresAt - 30000) {
    return relayToken;
  }

  if (!relayTokenPromise) {
    relayTokenPromise = bundledAxios.post('/xapi/session/swanbot-relay-token', null, {
      withCredentials: true
    }).then(function (response) {
      if (relayTokenOrganization !== organization) {
        throw new Error('Organization changed while authenticating. Retry the request.');
      }
      relayToken = response.data.token;
      relayTokenExpiresAt = Date.now() + (Number(response.data.expires_in) * 1000);
      return relayToken;
    }).finally(function () {
      relayTokenPromise = null;
    });
  }

  return relayTokenPromise;
}


function relayRequestUrl(path) {
  const relayUrl = window.WingmanState && window.WingmanState.swanbot_relay_url;
  return relayUrl ? relayUrl + path : path;
}


function toRelayUrl(url) {
  const baseUrl = window.WingmanState && window.WingmanState.swanbot_URL;
  if (!isSwanbotRelayEnabled() || typeof url !== 'string' || !baseUrl) {
    return null;
  }

  const target = new URL(url, window.location.origin);
  const base = new URL(baseUrl, window.location.origin);
  if (target.origin !== base.origin || !target.pathname.startsWith(base.pathname)) {
    return null;
  }

  const relativePath = target.pathname.slice(base.pathname.length).replace(/^\/+/, '');
  return relayRequestUrl('/xapi/swanbot/' + relativePath + target.search);
}


function installInterceptor(client) {
  if (!client || !client.interceptors || installedClients.has(client)) {
    return;
  }

  installedClients.add(client);
  client.interceptors.request.use(async function (config) {
    const relayUrl = toRelayUrl(config.url);
    if (!relayUrl) {
      return config;
    }

    const token = await getSwanbotRelayToken();
    config.url = relayUrl;
    config.baseURL = undefined;
    config.withCredentials = false;
    config.headers = config.headers || {};
    config.headers.Authorization = 'Bearer ' + token;
    return config;
  });
}


export function installSwanbotRelayTransport() {
  installInterceptor(bundledAxios);
  if (window.axios && window.axios !== bundledAxios) {
    installInterceptor(window.axios);
  }
}


export async function streamDataExplorerChat(upstreamUrl, options = {}) {
  const token = await getSwanbotRelayToken();
  const target = new URL(upstreamUrl, window.location.origin);
  const payload = {};
  target.searchParams.forEach(function (value, key) {
    if (!['key', 'orgID', 'customer_id', 'user_id'].includes(key)) {
      payload[key] = value;
    }
  });

  const response = await fetch(relayRequestUrl('/xapi/swanbot/chat/stream'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Authorization': 'Bearer ' + token,
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream'
    },
    body: JSON.stringify(payload),
    signal: options.signal
  });

  if (!response.ok || !response.body) {
    const error = new Error('Swanbot relay stream failed with status ' + response.status);
    error.response = { status: response.status };
    throw error;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let completeData = null;

  while (true) {
    const result = await reader.read();
    buffer += decoder.decode(result.value || new Uint8Array(), { stream: !result.done });
    const frames = buffer.split('\n\n');
    buffer = frames.pop() || '';

    for (const frame of frames) {
      const parsed = parseSseFrame(frame);
      if (!parsed) continue;
      if (parsed.event === 'error') {
        const error = new Error(parsed.data.detail || 'Swanbot relay error');
        error.response = { status: parsed.data.status || 502, data: parsed.data.detail || parsed.data };
        throw error;
      }
      if (parsed.event === 'complete') {
        completeData = parsed.data.response;
      }
      if (typeof options.onEvent === 'function') {
        options.onEvent(parsed.event, parsed.data);
      }
    }

    if (result.done) break;
  }

  if (!completeData) {
    throw new Error('Swanbot relay stream ended without a completion event');
  }
  return { status: 200, data: completeData };
}


function parseSseFrame(frame) {
  let event = 'message';
  const data = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    if (line.startsWith('data:')) data.push(line.slice(5).trim());
  }
  if (!data.length) return null;
  return { event, data: JSON.parse(data.join('\n')) };
}